import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    navigate(user.role === "admin" ? "/admin" : "/dashboard");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate(result.role === "admin" ? "/admin" : "/dashboard");
    } else {
      setError(result.error);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[80vh] flex items-center justify-center bg-[#F9F8F6] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-[#E68A00] rounded-md flex items-center justify-center mx-auto mb-4">
            <Truck className="w-6 h-6 text-white" />
          </div>
          <h1
            className="text-2xl font-bold text-[#1A2F24]"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Welcome Back
          </h1>
          <p className="text-sm text-[#595959] mt-1">Log in to manage your rentals</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-[#E5E0D8] rounded-lg p-6 space-y-4">
          {error && (
            <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md">
              {error}
            </div>
          )}
          <div>
            <Label htmlFor="email" className="text-[#1C1C1C]">Email</Label>
            <Input
              id="email"
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="mt-1.5 bg-white border-[#E5E0D8] focus:ring-[#E68A00]"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-[#1C1C1C]">Password</Label>
            <div className="relative mt-1.5">
              <Input
                id="password"
                data-testid="login-password"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                required
                className="bg-white border-[#E5E0D8] focus:ring-[#E68A00] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#595959]"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full bg-[#E68A00] hover:bg-[#CC7A00] disabled:opacity-60 text-white font-semibold py-3 rounded-md transition-colors"
          >
            {loading ? "Logging in..." : "Log In"}
          </button>
          <div className="flex items-center justify-between text-sm">
            <Link to="/forgot-password" className="text-[#595959] hover:text-[#E68A00] transition-colors" data-testid="forgot-password-link">
              Forgot password?
            </Link>
            <span className="text-[#595959]">
              No account?{" "}
              <Link to="/register" className="text-[#E68A00] font-medium hover:underline" data-testid="login-register-link">
                Sign Up
              </Link>
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
